/**
 * Keyboard Shortcuts Display Component
 * 
 * Shows all available keyboard shortcuts.
 * Ported from keyboard shortcuts panel in main.mxml
 */

import { isRealResource, isBuilding, isMilitary, BARRACKS, WATER, DEEP_WATER, CUTTER, QUARRY, MINE, FARM, SAWMILL, STONEMASON, FOUNDRY, MILL, MANOR, BAZAAR, WHARF, CASA, CELLARIUM, CACHE } from '../core/Constants.js';
import { canBuildBuilding } from '../core/BuildingData.js';
import { getTileImageKey } from '../utils/TileImageMap.js';
import { getImagePath } from '../utils/imagePath.js';

export class KeyboardShortcutsDisplay {
    constructor(containerElement, gameEngine) {
        this.container = containerElement;
        this.gameEngine = gameEngine;
        this.visible = true; // Show by default
        this.selectedKey = null; // Currently selected shortcut key
        this.quickbuildEnabled = true; // Quickbuild mode enabled/disabled
        this.onSelectionChange = null; // Callback when selection changes
        
        this.render();
    }
    
    /**
     * Toggle visibility
     */
    toggle() {
        this.visible = !this.visible;
        this.render();
    }
    
    /**
     * Show shortcuts
     */
    show() {
        this.visible = true;
        this.render();
    }
    
    /**
     * Hide shortcuts
     */
    hide() {
        this.visible = false;
        this.render();
    }
    
    /**
     * Render the component
     */
    render() {
        if (!this.visible) {
            this.container.innerHTML = `
                <button class="btn-show-shortcuts" id="btn-show-shortcuts">
                    Show Building Selector
                </button>
            `;
            
            const btn = document.getElementById('btn-show-shortcuts');
            if (btn) {
                btn.addEventListener('click', () => this.show());
            }
            return;
        }
        
        if (!this.gameEngine) return;
        
        // Group shortcuts by category
        const shortcuts = this.getShortcutsByCategory();
        
        this.container.innerHTML = `
            <div class="keyboard-shortcuts-panel">
                <button class="btn-close-shortcuts" id="btn-close-shortcuts" title="Collapse shortcuts">▲</button>
                <div class="shortcuts-header">
                    <h4>Building Selector</h4>
                </div>
                <p class="shortcuts-note">Click a shortcut key to quickbuild</p>
                
                <div class="quickbuild-status">
                    <div class="quickbuild-status-indicator ${this.quickbuildEnabled && this.selectedKey ? 'active' : 'inactive'}">
                        <span class="quickbuild-status-text">
                            ${this.quickbuildEnabled && this.selectedKey ? 'Quickbuild is now Active' : 'Quickbuild Disabled'}
                        </span>
                    </div>
                </div>
                
                <div class="shortcuts-content">
                    ${Object.entries(shortcuts).map(([category, items]) => `
                        <div class="shortcuts-category">
                            <h5>${category}</h5>
                            <div class="shortcuts-grid">
                                ${items.map(item => {
                                    let imageHtml = '';
                                    if (item.imageKey) {
                                        if (item.isAction) {
                                            // Actions use UI icons
                                            imageHtml = `
                                                <img src="${getImagePath(`ui/${this.getActionIcon(item.imageKey)}.png`)}" 
                                                     alt="${item.description}" 
                                                     class="shortcut-card-icon"
                                                     onerror="this.style.display='none'" />
                                            `;
                                        } else {
                                            // Buildings/resources use building images
                                            imageHtml = `
                                                <img src="${getImagePath(`buildings/${this.getImageFilename(item.imageKey)}.png`)}" 
                                                     alt="${item.description}" 
                                                     class="shortcut-card-icon"
                                                     onerror="this.style.display='none'" />
                                            `;
                                        }
                                    }
                                    const isSelected = this.selectedKey === item.key;
                                    return `
                                    <div class="shortcut-card ${isSelected ? 'selected' : ''}" data-key="${item.key}">
                                        <div class="shortcut-card-header">
                                            ${imageHtml}
                                        </div>
                                        <div class="shortcut-card-body">
                                            <div class="shortcut-card-name">${item.description}</div>
                                            <kbd class="shortcut-card-key">${this.formatKey(item.key)}</kbd>
                                        </div>
                                    </div>
                                `;
                                }).join('')}
                            </div>
                        </div>
                    `).join('')}
                </div>
            </div>
        `;
        
        // Setup close button
        const closeBtn = document.getElementById('btn-close-shortcuts');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.hide());
        }
        
        // Setup click handlers for shortcut cards
        this.container.querySelectorAll('.shortcut-card').forEach(item => {
            item.addEventListener('click', (e) => {
                const key = e.currentTarget.dataset.key;
                if (key) {
                    this.selectShortcut(key);
                }
            });
        });
        
        // Update status after render
        this.updateQuickbuildStatus();
    }
    
    /**
     * Select a shortcut (toggles if same key is clicked)
     */
    selectShortcut(key) {
        // Don't allow selecting action shortcuts
        const binding = this.gameEngine?.keyBindings[key];
        if (!binding) return;
        
        const [tile] = binding;
        // Don't select special actions (DUMMY tiles)
        if (tile >= 0x1ffff) return;
        
        // Toggle: if clicking the same key, deselect it
        if (this.selectedKey === key) {
            this.selectedKey = null;
        } else {
            this.selectedKey = key;
        }
        
        this.render();
        this.updateQuickbuildStatus();
        
        // Notify callback (pass null if deselected or if quickbuild is disabled)
        if (this.onSelectionChange) {
            if (this.selectedKey && this.quickbuildEnabled) {
                this.onSelectionChange(this.selectedKey);
            } else {
                this.onSelectionChange(null);
            }
        }
    }
    
    /**
     * Update quickbuild status display
     */
    updateQuickbuildStatus() {
        const statusIndicator = this.container.querySelector('.quickbuild-status-indicator');
        const statusText = this.container.querySelector('.quickbuild-status-text');
        
        if (statusIndicator && statusText) {
            const isActive = this.quickbuildEnabled && this.selectedKey;
            statusIndicator.classList.toggle('active', isActive);
            statusIndicator.classList.toggle('inactive', !isActive);
            statusText.textContent = isActive ? 'Quickbuild is now Active' : 'Quickbuild Disabled';
        }
    }
    
    /**
     * Clear selection
     */
    clearSelection() {
        this.selectedKey = null;
        this.render();
        this.updateQuickbuildStatus();
        
        // Notify callback
        if (this.onSelectionChange) {
            this.onSelectionChange(null);
        }
    }
    
    /**
     * Get currently selected key
     */
    getSelectedKey() {
        return this.selectedKey;
    }
    
    /**
     * Get shortcuts grouped by category
     */
    getShortcutsByCategory() {
        if (!this.gameEngine) return {};
        
        const categories = {
            'Resources': [],
            'Resources Buildings': [],
            'Logistical Buildings': [],
            'Military': [],
            'Actions': [],
            'Special': []
        };
        
        // Get current Basilica level
        const basilicaLevel = this.gameEngine?.basilicaLevel || 10;
        
        // Add building shortcuts from keyBindings
        for (const [key, binding] of Object.entries(this.gameEngine.keyBindings)) {
            const [tile, name, displayName] = binding;
            // Use displayName (3rd element) if available, fallback to name (2nd element)
            const description = displayName || name;
            
            // Get image key for buildings
            const imageKey = getTileImageKey(tile);
            
            // Handle special action shortcuts (DUMMY tiles)
            if (tile >= 0x1ffff) {
                // These are special actions - use icon from binding (2nd element)
                let actionIcon = null;
                if (key === '<space>') {
                    actionIcon = 'locked'; // Use locked icon for lock/unlock
                    categories['Special'].push({ key: 'Space', description: description, imageKey: actionIcon, isAction: true });
                } else if (key === '<shift>') {
                    actionIcon = 'summary_grid';
                    categories['Special'].push({ key: 'Shift', description: description, imageKey: actionIcon, isAction: true });
                } else if (key === '`, 0, or <del>') {
                    actionIcon = 'delete';
                    categories['Actions'].push({ key: '` or 0 or Del', description: description, imageKey: actionIcon, isAction: true });
                }
                continue;
            }
            
            // Hide water tiles (WATER and DEEP_WATER)
            if (tile === WATER || tile === DEEP_WATER) {
                continue; // Skip water tiles
            }
            
            // Categorize based on tile type using helper functions
            // Filter buildings based on Basilica level requirements
            if (isRealResource(tile)) {
                categories['Resources'].push({ key, description, imageKey });
            } else if (isMilitary(tile) || tile === BARRACKS) {
                // Include BARRACKS in Military even though isMilitary excludes it
                // Check if military building can be built
                if (canBuildBuilding(tile, basilicaLevel)) {
                    categories['Military'].push({ key, description, imageKey });
                }
            } else if (isBuilding(tile)) {
                // Check if building can be built
                if (canBuildBuilding(tile, basilicaLevel)) {
                    // Categorize buildings into subcategories
                    if (tile === CUTTER || tile === QUARRY || tile === MINE || tile === FARM || 
                        tile === SAWMILL || tile === STONEMASON || tile === FOUNDRY || tile === MILL ||
                        tile === MANOR) {
                        // Resources Buildings (harvesters, refiners, and gold producers)
                        categories['Resources Buildings'].push({ key, description, imageKey });
                    } else if (tile === BAZAAR || tile === WHARF || tile === CASA || tile === CELLARIUM || tile === CACHE) {
                        // Logistical Buildings (infrastructure)
                        categories['Logistical Buildings'].push({ key, description, imageKey });
                    }
                }
            }
        }
        
        // Add other action shortcuts
        categories['Actions'].push(
            { key: 'Enter', description: 'Place suggested building', imageKey: 'star_icon', isAction: true }
        );
        
        return categories;
    }
    
    /**
     * Get image filename from asset key
     */
    getImageFilename(key) {
        if (!key) return null;
        
        // Map asset keys to actual filenames (matching AssetLoader paths)
        const filenameMap = {
            // Buildings
            'building_townhall': 'basilica-fix-64',
            'building_forester': 'forester-v6-64',
            'building_sawmill': 'sawmill-64',
            'building_quarry': 'quarry-2-64',
            'building_stonemason': 'stonemasonery-64',
            'building_mine': 'iron-mine-64',
            'building_foundry': 'smelter-64',
            'building_farm': 'farm-v3-64',
            'building_mill': 'grain-mill-64-fix',
            'building_manor': 'villa-64',
            'building_bazaar': 'market-64',
            'building_wharf': 'ports-front-flip-64',
            'building_casa': 'basic_house-64',
            'building_cellarium': 'warehouse-v2-64',
            'building_cache': 'hideaway-64',
            'building_barracks': 'barracks-v2-64',
            'building_castle': 'castle-64-fix',
            'building_guard_house': 'guard\'s_house-v2-64',
            'building_shipyard': 'shipyard-front-flip-64',
            'building_moonglow': 'sorcerer\'s-tower-v2-64',
            'building_stable': 'stable-v2-fix-64',
            'building_temple': 'academy',
            'building_training_ground': 'training_arena-64',
            'building_workshop': 'blacksmith-v2-64',
            // Resources
            'resource_forest': 'forest-2-64',
            'resource_stones': 'stones-3-64',
            'resource_iron_node': 'iron-2-64',
            'resource_lake': 'lake-2-64'
        };
        
        // Check if it's a building or resource key
        if (key.startsWith('building_')) {
            return filenameMap[key] || key.replace('building_', '').replace(/_/g, '-') + '-64';
        } else if (key.startsWith('resource_')) {
            return filenameMap[key] || key.replace('resource_', '').replace(/_/g, '-') + '-64';
        }
        
        return filenameMap[key] || key;
    }
    
    /**
     * Get action icon filename
     */
    getActionIcon(iconKey) {
        const iconMap = {
            'locked': 'locked',
            'unlocked': 'unlocked',
            'summary_grid': 'summary_grid',
            'delete': 'delete',
            'star_icon': 'star_icon'
        };
        return iconMap[iconKey] || iconKey;
    }
    
    /**
     * Format key for display
     */
    formatKey(key) {
        const special = {
            ' ': 'Space',
            'Enter': 'Enter',
            'Delete': 'Del',
            'Backspace': 'Backspace',
            'Control': 'Ctrl',
            'Shift': 'Shift',
            'Alt': 'Alt',
            '`': '`'
        };
        
        return special[key] || key.toUpperCase();
    }
}

